/**
 * Getting the 30-minute silence warning (CLA-1564) to a clinician who is not
 * looking at the tab.
 *
 * A recording left running is, almost by definition, in a tab nobody is
 * watching — often a background one. The in-app prompt alone would sit
 * unseen, so the warning also:
 *  - prefixes the document title, which shows on the tab strip and in the
 *    window switcher;
 *  - raises a system notification, ONLY if the user has already granted
 *    notification permission. It never asks: a permission prompt fired by a
 *    silent background tab is exactly the kind of interruption that gets
 *    denied, and would cost the permission for everything else.
 *
 * Both are undone when the warning is answered or the recording ends. Every
 * browser API here is optional and fails quietly — a warning that cannot be
 * shown must never affect the recording.
 */

export const SILENCE_WARNING_TEXT = 'Still recording — no sound for 30 minutes.'
/** The second line, shared across platforms so the prompt reads the same everywhere. */
export const SILENCE_WARNING_DETAIL = 'If nothing is heard for an hour, the recording stops and is saved.'
const TITLE_PREFIX = '(No sound) '
const NOTIFICATION_TAG = 'claro-recording-silence'

let notification: Notification | null = null

export function raiseSilenceAlert(): void {
  try {
    if (typeof document !== 'undefined' && !document.title.startsWith(TITLE_PREFIX)) {
      document.title = TITLE_PREFIX + document.title
    }
  } catch {
    /* best-effort */
  }

  try {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return
    notification?.close()
    const n = new Notification(SILENCE_WARNING_TEXT, {
      body: SILENCE_WARNING_DETAIL,
      tag: NOTIFICATION_TAG,
      requireInteraction: true,
    })
    n.onclick = () => {
      try {
        window.focus()
      } catch {
        /* best-effort */
      }
      n.close()
    }
    notification = n
  } catch {
    // Some platforms (Chrome on Android) only allow notifications from a
    // service worker and throw here. The title and the in-app prompt remain.
    notification = null
  }
}

export function clearSilenceAlert(): void {
  try {
    if (typeof document !== 'undefined' && document.title.startsWith(TITLE_PREFIX)) {
      document.title = document.title.slice(TITLE_PREFIX.length)
    }
  } catch {
    /* best-effort */
  }
  try {
    notification?.close()
  } catch {
    /* best-effort */
  }
  notification = null
}
